<template>
  <div class="finance_box" ref="myElementRef">
    <div ref="searchBox">
      <div class="subnav-bar">
        <router-link class="rule-link" to="/attendance/annualLeaveRule">{{ t('attendance.annualLeaveRuleLink') }}</router-link>
      </div>
      <hc-search :data="searchData" @serch="getSearch" />
    </div>
    <div class="table_box">
      <hc-table :loading="state.loading" :tableData="state.tableData" :column="state.tableHeader" :offsetHeight="state.offsetHeight" :maxPage="maxPage"
        @pageChange="pageChange" @limitChange="limitChange">
        <template #start_date="{ row }">
          {{ formatStartDateCell(row) }}
        </template>
        <template #operation="{ row }">
          <el-button class="opBtn" size="small" type="primary" link @click="openEdit(row)">{{ t('attendance.configure') }}</el-button>
        </template>
      </hc-table>
    </div>

    <el-dialog v-model="editVisible" :title="t('attendance.annualLeaveBalance')" width="620" draggable>
      <el-form label-width="150px">
        <el-form-item :label="t('attendance.machinist')">{{ editForm.real_name || '-' }}</el-form-item>
        <el-form-item :label="t('attendance.startDateOnJob')"><span class="readonly-field">{{ editForm.start_date || t('attendance.notMaintained') }}</span></el-form-item>
        <el-form-item :label="t('attendance.leaveYear')">{{ editForm.leave_year }}</el-form-item>
        <el-form-item :label="t('attendance.currentYearDays')"><el-input v-model="editForm.current_year_days" disabled /></el-form-item>
        <el-form-item :label="t('attendance.carryoverDays')"><el-input v-model="editForm.carryover_days" /></el-form-item>
        <el-form-item :label="t('attendance.totalAnnualDays')"><el-input v-model="editForm.total_days" disabled /></el-form-item>
        <el-form-item :label="t('attendance.usedAnnualDays')"><el-input v-model="editForm.used_days" /></el-form-item>
        <el-form-item :label="t('attendance.remainAnnualDays')"><el-input v-model="editForm.remain_days" disabled /></el-form-item>
        <el-form-item :label="t('attendance.remark')"><el-input v-model="editForm.remark" type="textarea" :rows="2" maxlength="200" show-word-limit /></el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="editVisible=false">{{ t('common.cancel') }}</el-button>
        <el-button type="primary" @click="save">{{ t('common.save') }}</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import hcSearch from "@/components/hc-search/index.vue";
import hcTable from "@/components/hc-table/index.vue";
import { reactive, ref, nextTick, onMounted, onUnmounted, toRaw, watch } from "vue";
import { listToMap } from "@/utils/utils";
import { formatDisplayDate } from "@/utils/dateTime.js";
import { ElMessage } from "element-plus";
import { pageListApi, saveApi } from "@/api/attendance/annualLeaveBalance";
import { useAttendancePageI18n, t } from '@/composables/useAttendancePageI18n.js';

const myElementRef = ref();
const searchBox = ref();
const page = ref(1);
const limit = ref(20);
const maxPage = ref(0);
const currentYear = new Date().getFullYear();

const buildEmploymentOptions = () => ([
  { label: t('common.all'), value: 99 },
  { label: t('status.onJob'), value: 1 },
  { label: t('status.offJob'), value: 0 },
]);

const employmentOptions = reactive(buildEmploymentOptions());

const searchData = reactive([
  { label: "", type: "input", value: "", field: "keywords", placeholder: "" },
  { label: "", type: "select", value: 99, field: "employment", clearable: false, children: employmentOptions },
  { label: "", type: "input", value: String(currentYear), field: "leave_year", placeholder: "" },
]);

const buildColumns = () => ([
  { show: true, type: "index", label: t('table.index'), width: 80 },
  { prop: "real_name", show: true, label: t('field.realName'), width: 100 },
  { prop: "mobile", show: true, label: t('field.mobile'), width: 118 },
  { prop: "start_date", show: true, label: t('attendance.startDateOnJob'), width: 128 },
  { prop: "current_year_days", show: true, label: t('attendance.currentYearDays') },
  { prop: "carryover_days", show: true, label: t('attendance.carryoverDays') },
  { prop: "total_days", show: true, label: t('attendance.totalAnnualDays') },
  { prop: "used_days", show: true, label: t('attendance.usedAnnualDays') },
  { prop: "remain_days", show: true, label: t('attendance.remainAnnualDays') },
  { prop: "remark", show: true, label: t('attendance.remark') },
  { prop: "operation", show: true, label: t('table.operation') },
]);

const applyLabels = () => {
  searchData[0].label = t('attendance.machinistInfo');
  searchData[0].placeholder = t('attendance.placeholderNameUserMobile');
  searchData[1].label = t('field.employment');
  searchData[2].label = t('attendance.leaveYear');
  searchData[2].placeholder = t('attendance.yearPlaceholder');
  Object.assign(employmentOptions, buildEmploymentOptions());
  state.column = buildColumns();
  tableHeaderFn();
};

useAttendancePageI18n(applyLabels);

const state = reactive({
	loading: false,
  offsetHeight: 0,
  tableHeader: [],
  tableData: [],
  column: buildColumns(),
});

const editVisible = ref(false);
const formatStartDateCell = (row) => {
  const v = row?.start_date
  if (v == null || v === '') return t('attendance.notMaintained')
  return formatDisplayDate(v) || t('attendance.notMaintained')
}

const editForm = reactive({
  user_id: null, real_name: '', start_date: '', leave_year: currentYear,
  carryover_days: '0', used_days: '0', total_days: '0', remain_days: '0', current_year_days: '0', remark: ''
});

const toNumber = (val) => {
  const n = Number(val);
  return Number.isFinite(n) ? n : 0;
}
const calcAndFill = () => {
  const current = toNumber(editForm.current_year_days);
  const carry = toNumber(editForm.carryover_days);
  const used = Math.max(0, toNumber(editForm.used_days));
  const total = current + carry;
  const remain = Math.max(0, total - used);
  editForm.total_days = total.toFixed(2);
  editForm.remain_days = remain.toFixed(2);
}

const tableHeaderFn = () => state.tableHeader = state.column.filter(v => v.show);
const offHeighths = () => {
  if (myElementRef.value) state.offsetHeight = myElementRef.value?.offsetHeight - 90 - searchBox.value?.offsetHeight;
}
const pageChange = (p) => { page.value = p; getSearch(searchData); }
const limitChange = (l) => { limit.value = l; getSearch(searchData); }

const getSearch = (params = []) => {
  const form = listToMap(toRaw(params), { value: 'value', key: 'field' });
  const leaveYear = Number(form.leave_year || currentYear);
  const empRaw = form.employment;
  const employment = (empRaw === '' || empRaw === undefined || empRaw === null) ? 99 : Number(empRaw);
  const payload = JSON.stringify({ page: page.value, limit: limit.value, ...form, leave_year: leaveYear, employment });
  pageListApi(payload).then(res => {
    maxPage.value = res?.maxPage ?? 0;
    state.tableData = res?.data ?? [];
  });
}

const openEdit = (row) => {
  editForm.user_id = row.user_id;
  editForm.real_name = row.real_name;
  editForm.start_date = formatStartDateCell(row);
  editForm.leave_year = row.leave_year || currentYear;
  editForm.carryover_days = String(row.carryover_days ?? 0);
  editForm.used_days = String(row.used_days ?? 0);
  editForm.total_days = String(row.total_days ?? 0);
  editForm.remain_days = String(row.remain_days ?? 0);
  editForm.current_year_days = String(row.current_year_days ?? 0);
  editForm.remark = row.remark || '';
  calcAndFill();
  editVisible.value = true;
}

const save = () => {
  const payload = JSON.stringify({
    user_id: editForm.user_id,
    leave_year: Number(editForm.leave_year),
    carryover_days: editForm.carryover_days,
    used_days: editForm.used_days,
    total_days: editForm.total_days,
    remain_days: editForm.remain_days,
    remark: editForm.remark
  });
  saveApi(payload).then(res => {
    if (res?.code === 200) {
      ElMessage.success(t('attendance.saveSuccess'));
      editVisible.value = false;
      getSearch(searchData);
    } else {
      ElMessage.error(res?.msg || t('attendance.saveFailed'));
    }
  });
}

onMounted(() => {
  applyLabels();
  nextTick(() => offHeighths());
  window.onresize = () => nextTick(() => offHeighths());
  tableHeaderFn();
  getSearch(searchData);
});
onUnmounted(() => { window.onresize = null; });

watch(
  () => [editForm.current_year_days, editForm.carryover_days, editForm.used_days],
  () => calcAndFill(),
  { immediate: true }
);
</script>

<style lang="less" scoped>
.finance_box { flex: 1; overflow: auto; background-color: #fff; border-radius: 6px; padding: 10px; margin-right: 10px; }
.subnav-bar { margin-bottom: 8px; }
.rule-link {
  font-size: 14px;
  color: #266fed;
  text-decoration: none;
}
.rule-link:hover { text-decoration: underline; }
.readonly-field { color: #303133; }
.table_box { width: 100%; }
.opBtn { margin: 0 10px 0 0; }
</style>
